<template>
  <article class="metric-panel" :aria-labelledby="id" :aria-busy="loading">
    <header class="metric-heading">
      <div>
        <h3 :id="id">{{ title }}</h3>
        <p>{{ subtitle }}</p>
      </div>
      <button type="button" class="view-data" :aria-expanded="showTable" @click="toggleTable">
        {{ showTable ? 'Chart' : stacked ? 'View customers' : 'View data' }}
      </button>
    </header>
    <div v-if="loading" class="chart-state" role="status">Loading {{ title.toLowerCase() }}…</div>
    <div v-else-if="error" class="chart-state error" role="status">
      <i class="mdi mdi-alert-circle-outline" aria-hidden="true"></i>
      <p>This chart could not be loaded.</p>
      <button type="button" @click="$emit('retry')">Try again</button>
    </div>
    <div v-else-if="!rows.length" class="chart-state">No data for this period.</div>
    <template v-else>
      <div class="metric-value">
        <strong>{{ format(latest) }}</strong><span>{{ unit }}<small>{{ latestLabel }}</small></span>
      </div>
      <template v-if="showTable">
        <div v-if="stacked" class="customer-search">
          <label :for="`${id}-search`">Search customers</label>
          <input :id="`${id}-search`" v-model="search" type="search" placeholder="Customer name" />
          <span role="status">{{ filteredSeries.length }} of {{ series.length }} customers</span>
        </div>
        <div class="data-scroll" tabindex="0" :aria-label="`${title} data`">
          <table v-if="stacked">
            <caption>
              {{
                title
              }}
              by customer ·
              {{
                unit
              }}
              · ranked by selected period total
            </caption>
            <thead>
              <tr>
                <th scope="col">Customer</th>
                <th scope="col">Period total</th>
                <th v-for="row in rows" :key="row.label" scope="col">{{ monthLabel(row.label) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in filteredSeries" :key="s.name">
                <th scope="row">{{ s.name }}</th>
                <td>{{ format(sum(s.data)) }}</td>
                <td v-for="(value, index) in s.data" :key="index">{{ format(value) }}</td>
              </tr>
              <tr v-if="!filteredSeries.length">
                <td :colspan="rows.length + 2">No matching customers.</td>
              </tr>
            </tbody>
          </table>
          <table v-else>
            <caption class="visually-hidden">
              {{
                title
              }}
              by month
            </caption>
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">{{ title }} {{ unit }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="row.label">
                <th scope="row">{{ monthLabel(row.label) }}</th>
                <td>{{ format(series[0].data[index] ?? 0) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <div
        v-else
        class="chart-wrap"
        role="img"
        :aria-label="`${title}. Monthly chart; select ${stacked ? 'View customers' : 'View data'} for exact values.`"
      >
        <apexchart :type="type" height="230" :options="options" :series="chartSeries" />
      </div>
      <div v-if="summary && !showTable" class="period-summary">
        <dl>
          <div v-for="stat in summaryStats" :key="stat.label">
            <dt>{{ stat.label }}</dt>
            <dd>
              {{ stat.value }}<small>{{ stat.detail }}</small>
            </dd>
          </div>
        </dl>
        <p>
          {{
            summary === 'shipments'
              ? 'Period total includes the current month to date. Average and peak use completed months.'
              : 'Based on monthly average prices for completed months in the selected period.'
          }}
        </p>
      </div>
      <template v-if="stacked && !showTable">
        <ul class="customer-legend" aria-label="Customers shown in chart">
          <li v-for="(customer, index) in chartSeries" :key="customer.name">
            <span class="legend-swatch" :style="{ background: chartColors[index] }" aria-hidden="true"></span>
            <span>{{ customer.name }}</span>
          </li>
        </ul>
        <p class="chart-note">
          Each month shows its top 5 active customers; the rest are grouped as Other. All customers are included in the
          totals.
        </p>
      </template>
    </template>
  </article>
</template>
<script lang="ts">
import { computed, defineComponent, ref, type PropType } from 'vue'
import { buildChartTooltip } from '@/utils/chart-tooltip'
import { customerColor, monthlyCustomerRows, monthlyCustomerSeries, otherCustomersColor } from '@/utils/customer-chart'
import type { ApexOptions } from 'apexcharts'
import type { Periods } from '@/typings/Periods'
import type { CustomerMonthRow, CustomerSeries } from '@/typings/CustomerChart'

export default defineComponent({
  name: 'OverviewChart',
  props: {
    id: { type: String, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    data: { type: Object as PropType<Periods>, required: true },
    summary: { type: String as PropType<'shipments' | 'price'>, default: undefined },
    unit: { type: String, default: '' },
    stacked: { type: Boolean, default: false },
    type: { type: String as PropType<'bar' | 'area'>, default: 'bar' },
    loading: { type: Boolean, required: true },
    error: { type: Boolean, required: true },
  },
  emits: ['retry'],
  setup(props) {
    const showTable = ref<boolean>(false)
    const search = ref<string>('')
    function toggleTable(): void {
      showTable.value = !showTable.value
    }
    const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0)
    const rows = computed<{ label: string; value: unknown }[]>(() =>
      props.data.periodLabels.map((label, index) => ({ label: String(label), value: props.data.currentPeriod[index] })),
    )
    const amount = (v: unknown): number => {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }
    const series = computed<CustomerSeries[]>(() => {
      if (!props.stacked) return [{ name: props.title, data: rows.value.map((row) => amount(row.value)) }]
      const names = [
        ...new Set(
          rows.value.flatMap((row) => (row.value && typeof row.value === 'object' ? Object.keys(row.value) : [])),
        ),
      ]
      return names.map((name) => ({
        name,
        data: rows.value.map((row) =>
          amount(row.value && typeof row.value === 'object' ? (row.value as Record<string, unknown>)[name] : 0),
        ),
      }))
    })
    const rankedSeries = computed<CustomerSeries[]>(() =>
      [...series.value].sort((a, b) => sum(b.data) - sum(a.data) || a.name.localeCompare(b.name)),
    )
    const filteredSeries = computed<CustomerSeries[]>(() =>
      rankedSeries.value.filter((s) => s.name.toLocaleLowerCase().includes(search.value.trim().toLocaleLowerCase())),
    )
    const monthlyRows = computed<CustomerMonthRow[][]>(() =>
      rows.value.map((_, index) => monthlyCustomerRows(series.value, index)),
    )
    const chartSeries = computed<CustomerSeries[]>(() => {
      if (!props.stacked) return series.value
      return monthlyCustomerSeries(rankedSeries.value, monthlyRows.value)
    })
    const chartColors = computed<string[]>(() =>
      chartSeries.value.map((customer) => (customer.grouped ? otherCustomersColor : customerColor(customer.name))),
    )
    const latest = computed<number>(() => series.value.reduce((total, s) => total + (s.data.at(-1) ?? 0), 0))
    const monthLabel = (v: string): string => {
      const d = new Date(v)
      return Number.isNaN(d.getTime())
        ? v
        : new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(d)
    }
    const latestLabel = computed<string>(() => monthLabel(rows.value.at(-1)?.label ?? ''))
    const format = (value: number): string =>
      new Intl.NumberFormat('en-GB', { maximumFractionDigits: props.unit === 'kr' ? 2 : 0 }).format(value)
    const summaryStats = computed<{ label: string; value: string; detail: string }[]>(() => {
      const now = new Date()
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
      const values = rows.value
        .filter((row) => row.value !== null && row.value !== undefined && row.value !== '')
        .map((row) => ({ label: row.label, date: new Date(row.label).getTime(), value: Number(row.value) }))
        .filter((row) => Number.isFinite(row.value) && Number.isFinite(row.date))
      const completed = values.filter((row) => row.date < thisMonth)
      const high = completed.reduce<(typeof completed)[number] | undefined>(
        (best, row) => (!best || row.value > best.value ? row : best),
        undefined,
      )
      const low = completed.reduce<(typeof completed)[number] | undefined>(
        (best, row) => (!best || row.value < best.value ? row : best),
        undefined,
      )
      const last = [...completed].sort((a, b) => b.date - a.date)[0]
      const monthStat = (label: string, row: typeof high) => ({
        label,
        value: row ? `${format(row.value)}${props.unit === 'kr' ? ' kr' : ''}` : '—',
        detail: row ? monthLabel(row.label) : 'No completed months',
      })
      if (props.summary === 'price')
        return [
          monthStat('Lowest monthly price', low),
          monthStat('Highest monthly price', high),
          monthStat('Last complete month', last),
        ]
      return [
        { label: 'Period total', value: format(sum(values.map((row) => row.value))), detail: 'shipments' },
        {
          label: 'Monthly average',
          value: completed.length ? format(sum(completed.map((row) => row.value)) / completed.length) : '—',
          detail: completed.length ? `${completed.length} completed months` : 'No completed months',
        },
        monthStat('Peak month', high),
      ]
    })
    const options = computed<ApexOptions>(() => ({
      chart: {
        fontFamily: 'PacSort TT Norms, Arial, sans-serif',
        foreColor: '#667778',
        stacked: props.stacked,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        parentHeightOffset: 0,
      },
      colors: props.stacked ? chartColors.value : ['#147c73'],
      plotOptions: { bar: { columnWidth: '48%', borderRadius: 3 } },
      dataLabels: { enabled: false },
      stroke: { width: props.type === 'area' ? 2 : 0, curve: 'straight' },
      fill:
        props.type === 'area' ? { type: 'gradient', gradient: { opacityFrom: 0.22, opacityTo: 0.02 } } : { opacity: 1 },
      grid: { borderColor: '#edf1f1', strokeDashArray: 4, padding: { left: 8, right: 12 } },
      xaxis: {
        categories: rows.value.map((r) => r.label),
        tickAmount: 6,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          rotate: 0,
          hideOverlappingLabels: true,
          trim: false,
          style: { fontSize: '12px' },
          formatter: (value: string) => {
            const date = new Date(value)
            return Number.isNaN(date.getTime())
              ? value
              : new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(date)
          },
        },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          minWidth: 30,
          maxWidth: 62,
          formatter: (v: number) =>
            new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(v),
        },
      },
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) =>
          buildChartTooltip(
            props.title,
            monthLabel(rows.value[dataPointIndex]?.label ?? ''),
            props.unit,
            props.stacked
              ? (monthlyRows.value[dataPointIndex] ?? [])
              : [{ name: props.title, value: series.value[0]?.data[dataPointIndex] ?? 0, color: '#147c73' }],
            props.stacked,
          ),
        x: { formatter: (_v: number, ctx: any) => monthLabel(rows.value[ctx.dataPointIndex]?.label ?? '') },
        y: { formatter: (v: number) => `${format(v)} ${props.unit}` },
      },
      responsive: [{ breakpoint: 600, options: { xaxis: { tickAmount: 4 } } }],
    }))
    return {
      showTable,
      toggleTable,
      search,
      rows,
      series,
      filteredSeries,
      chartSeries,
      chartColors,
      latest,
      latestLabel,
      monthLabel,
      format,
      sum,
      summaryStats,
      options,
    }
  },
})
</script>
